import { Component, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { Company } from 'app/models/company.models';
import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CustomerRule, CustomerRuleUpdate } from 'app/models/customer-rule';
import { ApiService } from 'app/services/api.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CustomerCallListUpdate } from 'app/models/call-list-item';
import { forkJoin, Observable } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { RemoveEmaacItemDialogComponent } from './remove-emaac-item-dialog/remove-emaac-item-dialog.component';

interface AreaUI {
    area: string;
    rows: {
      callListId?: number;
      phoneNumber: string;
      timeRange: string;
    }[];
  }

@Component({
    selector: 'app-company-emaac-info',
    templateUrl: './company-emaac-info.component.html',
    styleUrls: ['./company-emaac-info.component.css'],
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [
    TranslateModule,
    FormsModule
]
})
export class CompanyEmaacInfoComponent implements OnChanges {
    constructor(private api: ApiService, private dialog: MatDialog, private translate: TranslateService) {}

    @Input() company!: Company;
    rules: CustomerRule[] = [];
    areas: AreaUI[] = [];
    loading = true;

    /** ids whose delete request is in flight; kept out of reloads so they don't reappear. */
    private pendingRuleDeletes = new Set<number>();
    private pendingCallListDeletes = new Set<number>();

    ngOnChanges(changes: SimpleChanges) {
      const company = changes['company']?.currentValue;
    
      if (!company?.companyId) return;
    
      this.loadData(company.companyId);
    }
  
    addRule() {
     this.rules.push({
        ruleId: undefined,
        subject: '',
        description: ''
     });
    }

    addArea() {
        this.areas.push({
          area: '',
          rows: [
            {
              phoneNumber: '',
              timeRange: ''
            }
          ]
        });
      }

      /**
       * A saved rule is deleted from the backend right away;
       * an unsaved one only from the list. Other unsaved edits on the page are kept.
       * Saved rules are matched by ruleId, since a save may reload the list while the dialog is open.
       * The rule leaves the list before the request and stays out of reloads while the delete is pending,
       * so a concurrent save can't re-add it; it is put back if the delete fails.
       */
      removeRule(rule: CustomerRule) {
        this.confirmRemove('REMOVE_RULE_CONFIRM').subscribe(confirmed => {
          if (!confirmed) return;

          const ruleId = rule.ruleId;
          const matches = (r: CustomerRule) => ruleId == null ? r === rule : r.ruleId === ruleId;

          const index = this.rules.findIndex(matches);
          if (index === -1) return;
          this.rules = this.rules.filter(r => !matches(r));

          if (ruleId == null) return;

          this.pendingRuleDeletes.add(ruleId);
          this.api
            .saveCustomerRules(this.company.companyId, { addOrUpdate: [], deleteRuleIds: [ruleId] })
            .pipe(finalize(() => this.pendingRuleDeletes.delete(ruleId)))
            .subscribe({
              error: (err) => {
                console.error('EMAAC rule remove failed', err);
                if (!this.rules.some(matches)) {
                  this.rules = this.restoreAt(this.rules, rule, index);
                }
              }
            });
        });
      }

      /** removes an area (all of its phone rows) after confirmation, like removeRule; saved rows are matched by callListId. */
      removeArea(area: AreaUI) {
        this.confirmRemove('REMOVE_AREA_CONFIRM').subscribe(confirmed => {
          if (!confirmed) return;

          const savedIds = area.rows
            .map(row => row.callListId)
            .filter((id): id is number => id != null);
          const isRemovedRow = (row: AreaUI['rows'][number]) => row.callListId != null && savedIds.includes(row.callListId);
          const matches = (a: AreaUI) => a === area || a.rows.some(isRemovedRow);

          const index = this.areas.findIndex(matches);
          if (index === -1) return;

          // a reloaded area may also hold rows added since; drop only the removed ones from it
          this.areas = this.areas
            .filter(a => a !== area)
            .map(a => a.rows.some(isRemovedRow) ? { ...a, rows: a.rows.filter(row => !isRemovedRow(row)) } : a)
            .filter(a => a.rows.length > 0);

          if (savedIds.length === 0) return;

          savedIds.forEach(id => this.pendingCallListDeletes.add(id));
          this.api
            .saveCustomerCallList(this.company.companyId, { addOrUpdate: [], deleteItemIds: savedIds })
            .pipe(finalize(() => savedIds.forEach(id => this.pendingCallListDeletes.delete(id))))
            .subscribe({
              error: (err) => {
                console.error('EMAAC area remove failed', err);
                if (!this.areas.some(matches)) {
                  this.areas = this.restoreAt(this.areas, area, index);
                }
              }
            });
        });
      }

      /** puts a removed item back at its original position. */
      private restoreAt<T>(list: T[], item: T, index: number): T[] {
        const copy = [...list];
        copy.splice(Math.min(index, copy.length), 0, item);
        return copy;
      }

      private confirmRemove(messageKey: string): Observable<boolean> {
        return this.dialog
          .open(RemoveEmaacItemDialogComponent, {
            maxWidth: '400px',
            ariaLabel: this.translate.instant(messageKey),
            data: { messageKey }
          })
          .afterClosed()
          .pipe(map(confirmed => confirmed === true));
      }

      addPhoneRow(areaIndex: number) {
        this.areas[areaIndex].rows.push({
          phoneNumber: '',
          timeRange: ''
        });
      }
  
    save() {
        const body: CustomerRuleUpdate = {
          addOrUpdate: [],
          deleteRuleIds: []
        };
      
        for (const rule of this.rules) {
          const hasContent =
            rule.subject?.trim() && rule.description?.trim();
      
          if (hasContent) {
            body.addOrUpdate.push({
              ruleId: rule.ruleId,
              subject: rule.subject,
              description: rule.description
            });
          } else if (rule.ruleId) {
            body.deleteRuleIds.push(rule.ruleId);
          }
        }
      
        this.api
          .saveCustomerRules(this.company.companyId, body)
          .subscribe(() => {
            this.loadData(this.company.companyId);
          });
      }

      saveCallListItems() {
        const body: CustomerCallListUpdate = {
          addOrUpdate: [],
          deleteItemIds: []
        };
      
        for (const area of this.areas) {
      
          for (const row of area.rows) {
      
            const parsed = this.parseTimeRange(row.timeRange);
            const normalizedPhone = this.normalizePhone(row.phoneNumber);

            const hasContent =
            area.area?.trim() &&
            row.phoneNumber?.trim() &&
            this.isValidPhone(row.phoneNumber) &&
            parsed.start &&
            parsed.end;

            if (!hasContent) {
            if (row.callListId) {
                body.deleteItemIds.push(row.callListId);
            }
            continue;
            }

            body.addOrUpdate.push({
            callListId: row.callListId,
            customerId: this.company.companyId,
            area: area.area,
            phoneNumber: normalizedPhone,
            callWindowStart: parsed.start,
            callWindowEnd: parsed.end
            });
          }
        }
      
        this.api.saveCustomerCallList(this.company.companyId, body)
          .subscribe(() => {
            this.loadData(this.company.companyId);
          });
      }

      private loadData(companyId: string) {
        this.loading = true;
        this.rules = [];
        this.areas = [];
        forkJoin({
          rules: this.api.getCompanyRules(companyId, this.company.server),
          callList: this.api.getCustomerCallList(companyId, this.company.server)
        })
        .pipe(finalize(() => this.loading = false))
        .subscribe({
          next: (res) => {
            this.rules = res.rules.filter(r => r.ruleId == null || !this.pendingRuleDeletes.has(r.ruleId));

            const grouped: { [key: string]: AreaUI } = {};

            for (const item of res.callList) {
              if (this.pendingCallListDeletes.has(item.callListId)) continue;

              if (!grouped[item.area]) {
                grouped[item.area] = {
                  area: item.area,
                  rows: []
                };
              }
      
              grouped[item.area].rows.push({
                callListId: item.callListId,
                phoneNumber: item.phoneNumber,
                timeRange: this.formatTimeRange(
                  item.callWindowStart,
                  item.callWindowEnd
                )
              });
            }
      
            this.areas = Object.values(grouped);
          },
          error: (err) => {
            console.error('EMAAC load failed', err);
            this.rules = [];
            this.areas = [];
          }
        });
      }

      private normalizeTime(time: string): string {
        if (!time) return time;
        return time.length === 5 ? `${time}:00` : time;
      }

      private parseTimeRange(range: string): { start: string; end: string } {
        if (!range) return { start: '', end: '' };
      
        const parts = range.split('-');
      
        return {
          start: this.normalizeTime(parts[0]?.trim()),
          end: this.normalizeTime(parts[1]?.trim())
        };
      }
      private formatTimeRange(start: string, end: string): string {
        return `${start?.substring(0, 5)}-${end?.substring(0, 5)}`;
      }

      private normalizePhone(input: string): string {
        if (!input) return '';

        let phone = input.replace(/[\s()-]/g, '');

        if (phone.startsWith('00')) {
            phone = '+' + phone.slice(2);
        }
        if (phone.startsWith('0')) {
            phone = '+358' + phone.slice(1);
        }

        return phone;
        }

    isValidPhone(input: string): boolean {
        if (!input?.trim()) return true;
          
        const phone = this.normalizePhone(input);
          
        if (!phone.startsWith('+')) return false;
        if (!/^\+[0-9]+$/.test(phone)) return false;
          
        const digits = phone.replace('+', '');
          
        return digits.length >= 10 && digits.length <= 15;
    }

    isCallListValid(): boolean {
      return this.areas.every(area =>
      area.rows.every(row => this.isValidPhone(row.phoneNumber))
      );
  }
}